const { useMemo, useState, useRef } = wp.element;
// import Tabs from './TabComponent/Tabs';
import LoadingSpinner from './LoadingSpinner';
import useFetchUsers from './hooks/useFetchUsers';

const UserManagementPage = () => {
  const currentUserFirstName = window.wpData.currentUserFirstName;
  const currentUserId = window.wpData.currentUserId;
  const companyId = window.wpData.companyId;
  const coLogo = window.wpData.coLogo;
  const templateDir = window.wpData.templateDir;
  const [refreshKey, setRefreshKey] = useState(0);

  const searchInputRef = useRef(null);

  const { allUsers, isLoading, error } = useFetchUsers(
    companyId,
    refreshKey
  );

  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'ascending',
  });
  const [searchTags, setSearchTags] = useState([]);

  const handleTagInputKeyUp = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      addTag(event.target.value);
      event.target.value = '';
    }
  };

  const handleSearchClick = (event) => {
    event.preventDefault();
    addTag(searchInputRef.current.value);
    searchInputRef.current.value = '';
  };

  const handleRefreshClick = () => {
    setRefreshKey((prevKey) => prevKey + 1);
  };

  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const addTag = (input) => {
    const extractTerms = (input) => {
      const terms = [];
      const regex = /"([^"]+)"|([^,]+)/g;
      let match;

      while ((match = regex.exec(input)) !== null) {
        // This is necessary to avoid infinite loops with zero-width matches
        if (match.index === regex.lastIndex) {
          regex.lastIndex++;
        }

        // Match 1 is for quoted terms, Match 2 is for the entire input if no quotes
        const term = match[1] ? match[1].trim() : match[2].trim();
        if (term) {
          terms.push(term.toLowerCase());
        }
      }

      return terms;
    };

    // Directly use the input to handle as a single term if not explicitly split by commas
    const terms = extractTerms(input);
    terms.forEach((term) => {
      if (term && !searchTags.includes(term)) {
        setSearchTags((prevTags) => [...prevTags, term]);
      }
    });
  };

  const removeTag = (tagToRemove) => {
    setSearchTags((prevTags) => prevTags.filter((tag) => tag !== tagToRemove));
  };

  const filteredUsers = useMemo(() => {
    return allUsers.filter(
      (user) =>
        searchTags.length === 0 ||
        searchTags.some(
          (tag) =>
            user.name.toLowerCase().includes(tag) ||
            user.email.toLowerCase().includes(tag) ||
            user.role.toLowerCase().includes(tag) ||
            user.phone_number.toLowerCase().includes(tag)
        )
    );
  }, [allUsers, searchTags]);

  const sortedUsers = useMemo(() => {
    let sortableUsers = [...filteredUsers];
    if (sortConfig.key !== null) {
      sortableUsers.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableUsers;
  }, [filteredUsers, sortConfig]);

  const formatUserRole = (role) => {
    if (typeof role !== 'string' || !role || role === null || role === undefined || role === '') {
      return 'No assigned role';
    }
    if (role === 'company-admin') {
      return 'Company Administrator';
    } else if (role === 'company-rep') {
      return 'Company Representative';
    } else {
      return role;
    }
  };

  return (
    <>
      <div class='ccare__inner-content'>
        <main className='main ccare__layout' role='main'>
          <div className='ccare__acct-header'>
            <div className='acct-info'>
              <h2 className='h1'>Hi, {currentUserFirstName}</h2>
              {coLogo && (
                <div className='ccare__company-logo-container'>
                  <div
                    className='ccare__company-logo'
                    style={{ backgroundImage: `url(${coLogo})` }}
                    alt='Company Logo'
                  ></div>
                </div>
              )}
            </div>
            <div className='ccare__layout-row cta'>
              <button
                id='create-new-user-btn'
                data-open='new-user-modal'
                className=' ccare__cta-user btn'
              >
                Create New User
              </button>
            </div>
          </div>
          <div id='user-section' className='ccare__user-section'>
            <div className='ccare__user-header'>
              <h3 className='ccare__users-title'>Users</h3>
            </div>
            <div className='ccare__layout-row buttons'>
              <div className='ccare__layout-section'>
                <div className='ccare__search-container users'>
                  <input
                    ref={searchInputRef}
                    className='ccare__search-input'
                    type='search'
                    placeholder='Search...'
                    id='user-search-field'
                    onKeyUp={handleTagInputKeyUp}
                  />
                  <button
                    className='ccare__search-btn'
                    id='user-search-btn'
                    onClick={handleSearchClick}
                  ></button>
                </div>
                <button
                  type='button'
                  id='user-refresh-btn'
                  className='ccare__refresh-btn users'
                  onClick={handleRefreshClick}
                >
                  <img
                    className='user-refresh-btn-icon'
                    src={`${templateDir}/assets/images/svgs/refresh-btn.svg`}
                    alt='refresh table data'
                  />
                  <span>Refresh Table</span>
                </button>
              </div>
            </div>

            <div
              id='user-tags-container'
              className={`ccare__search-tags ${
                searchTags.length === 0 ? 'empty' : ''
              }`}
            >
              {searchTags.map((tag, index) => (
                <div key={index} className='ccare__search-tag'>
                  <span>
                    Showing results for{' '}
                    <span className='ccare__tag'>{tag}</span>
                  </span>
                  <button className='tag-close' onClick={() => removeTag(tag)}>
                    <svg
                      width='20'
                      height='20'
                      viewBox='0 0 20 20'
                      fill='none'
                      xmlns='http://www.w3.org/2000/svg'
                    >
                      <path
                        d='M9.99935 18.3334C5.39697 18.3334 1.66602 14.6024 1.66602 10C1.66602 5.39765 5.39697 1.66669 9.99935 1.66669C14.6017 1.66669 18.3327 5.39765 18.3327 10C18.3327 14.6024 14.6017 18.3334 9.99935 18.3334ZM9.99935 8.82152L7.64232 6.46449L6.46382 7.64299L8.82085 10L6.46382 12.357L7.64232 13.5355L9.99935 11.1785L12.3563 13.5355L13.5348 12.357L11.1778 10L13.5348 7.64299L12.3563 6.46449L9.99935 8.82152Z'
                        fill='#535557'
                      />
                    </svg>
                  </button>
                </div>
              ))}
            </div>

            <div className='user-table-container'>
              <div id='user-table' className='ccare__table-user-wrapper'>
                <table
                  border='0'
                  cellPadding='0'
                  cellSpacing='0'
                  width='100%'
                  className='ccare__table-user'
                >
                  <thead id='table-header'>
                    <tr>
                      <th
                        className='ccare__user'
                        onClick={() => handleSort('name')}
                      >
                        <span>Name</span>
                        <img
                          src={`${templateDir}/assets/images/svgs/toggle-arrow.svg`}
                        />
                      </th>
                      <th
                        className='ccare__user'
                        onClick={() => handleSort('email')}
                      >
                        <span>Email Address</span>
                        <img
                          src={`${templateDir}/assets/images/svgs/toggle-arrow.svg`}
                        />
                      </th>
                      <th
                        className='ccare__user'
                        onClick={() => handleSort('phone_number')}
                      >
                        <span>Phone Number</span>
                        <img
                          src={`${templateDir}/assets/images/svgs/toggle-arrow.svg`}
                        />
                      </th>
                      <th
                        className='ccare__user'
                        onClick={() => handleSort('role')}
                      >
                        <span>Role</span>
                        <img
                          src={`${templateDir}/assets/images/svgs/toggle-arrow.svg`}
                        />
                      </th>

                      <th className='ccare__user'>
                        <span>Action</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody id='table-body'>
                    {sortedUsers.map((user) => {
                      const userIdStr = String(user.id);
                      const currentUserIdStr = String(currentUserId);

                      return (
                        <tr key={user.id} id={`ccare__user-row-${user.id}`}>
                          <td className='ccare__user'>{user.name}</td>
                          <td className='ccare__user'>{user.email}</td>
                          <td className='ccare__user'>{user.phone_number}</td>
                          <td className='ccare__user'>
                            {formatUserRole(user.role)}
                          </td>
                          <td className='ccare__user-action-btns'>
                            <button
                              // data-open='user-management-modal'
                              className='ccare__edit-user-btn'
                              data-user-id={user.id}
                              data-user-email={user.email}
                            >
                              <img
                                src={`${templateDir}/assets/images/svgs/edit.svg`}
                              />
                            </button>

                            {userIdStr !== currentUserIdStr && (
                              <button
                                data-open='delete-user-confirmation'
                                className='ccare__delete-user-btn'
                                data-user-id={user.id}
                              >
                                <img
                                  src={`${templateDir}/assets/images/svgs/delete.svg`}
                                />
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <LoadingSpinner />
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default UserManagementPage;
